import React, {useState, useEffect} from 'react';
import {
  Animated,
  StyleSheet,
  View,
  Text,
  Keyboard,
  Platform,
} from 'react-native';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {isTablet} from 'react-native-device-info';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Orientation from 'react-native-orientation-locker';
import {
  setIsMessagePanelOpen,
  setIsMessageNotification,
} from 'store/world/actions';
import IconButton from 'components/IconButton';

const FULL_WIDTH = Platform.OS === 'web' || isTablet() ? 375 : 300;
const TAB_WIDTH = 56;
const CONTENT_WIDTH = FULL_WIDTH - TAB_WIDTH;
const MESSAGE_PANEL_INDEX = 1;

const SidePanel = props => {
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();

  const isMessagePanelOpen = useSelector(
    state => state.world.isMessagePanelOpen,
  );
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [panelPosition] = useState(new Animated.Value(CONTENT_WIDTH));
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const [currentOrientation, setCurrentOrientation] = useState(
    Orientation.getInitialOrientation(),
  );

  useEffect(() => {
    const onOrientationDidChange = orientation => {
      if (currentOrientation !== orientation) {
        setCurrentOrientation(orientation);
      }
    };
    Orientation.addOrientationListener(onOrientationDidChange);

    return () => {
      Orientation.removeOrientationListener(onOrientationDidChange);
    };
  }, [currentOrientation]);

  // Dynamic safe-area insets — replaces hardcoded notch offsets that were
  // calibrated for older iPhones. Pulls the panel inward by the actual
  // right-side safe area inset so it sits flush against the safe edge
  // regardless of device (iPhone X, 11, 12, 13, 14, 15 etc).
  const marginRight = insets.right;
  const contentContainerPaddingLeft =
    !isPanelOpen && currentOrientation === 'LANDSCAPE-RIGHT' ? insets.left : 0;

  const {style, children} = props;

  let selected;
  if (Array.isArray(children)) {
    if (selectedIndex < children.length) {
      selected = children[selectedIndex];
    } else {
      [selected] = children;
    }
  } else {
    selected = children;
  }

  const {title, icon, color} = selected.props;

  const openSidebar = () => {
    setIsPanelOpen(true);

    Animated.spring(panelPosition, {
      toValue: 0,
      duration: 1000,
      overshootClamping: true,
      useNativeDriver: true,
    }).start();
  };

  const closeSidebar = () => {
    setIsPanelOpen(false);
    dispatch(setIsMessagePanelOpen(false));

    Keyboard.dismiss();

    Animated.spring(panelPosition, {
      toValue: CONTENT_WIDTH,
      duration: 1000,
      overshootClamping: true,
      useNativeDriver: true,
    }).start();
  };

  selected = React.cloneElement(selected, {
    closeSidebar,
  });

  const onButtonPressed = index => {
    const isMessagePanelSelected = index === MESSAGE_PANEL_INDEX;

    if (isPanelOpen && index === selectedIndex) {
      closeSidebar();
    } else {
      // check if it's the message panel
      if (isMessagePanelSelected) {
        dispatch(setIsMessageNotification(false));
      }
      dispatch(setIsMessagePanelOpen(isMessagePanelSelected));
      // update to the new selection
      setSelectedIndex(index);
      openSidebar();
    }
  };

  useEffect(() => {
    if (isMessagePanelOpen) {
      dispatch(setIsMessageNotification(false));
      setSelectedIndex(MESSAGE_PANEL_INDEX);
      openSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMessagePanelOpen]);

  const buttonList = [];
  if (Array.isArray(children)) {
    for (let i = 0; i < children.length; i += 1) {
      const child = children[i];
      const buttonData = {
        index: i,
        icon: child.props.icon,
        color: child.props.color,
        textBadge: child.props.textBadge,
        iconBadge: child.props.iconBadge,
        badgeStyle: child.props.badgeStyle,
        badgeForegroundColor: child.props.badgeForegroundColor,
        badgeBackgroundColor: child.props.badgeBackgroundColor,
        badgeContent: child.props.badgeContent,
        key: `key${i}`,
      };
      buttonList.push(buttonData);
    }
  }

  return (
    <View style={styles.container} pointerEvents="box-none">
      <View
        style={[{marginRight}, styles.panel, style]}
        pointerEvents="box-none"
      >
        <Animated.View
          style={[
            styles.animatedArea,
            {
              transform: [
                {
                  translateX: panelPosition,
                },
              ],
            },
          ]}
          pointerEvents="box-none"
        >
          <View
            style={[styles.fillUpperNotch, {backgroundColor: color}]}
            pointerEvents="box-none"
          />
          <View style={styles.tabContainer} pointerEvents="box-none" buttons>
            {buttonList.map(info => {
              return (
                <IconButton
                  name={info.icon}
                  onPress={() => {
                    onButtonPressed(info.index);
                  }}
                  color={info.color}
                  key={info.key}
                  style={styles.tabButton}
                  textBadge={info.textBadge}
                  iconBadge={info.iconBadge}
                  badgeForegroundColor={info.badgeForegroundColor}
                  badgeBackgroundColor={info.badgeBackgroundColor}
                  badgeStyle={info.badgeStyle}
                  badgeContent={info.badgeContent}
                />
              );
            })}
          </View>
          <View
            style={[
              styles.contentContainer,
              {paddingLeft: contentContainerPaddingLeft},
            ]}
            pointerEvents="box-none"
          >
            <View style={[styles.header, {backgroundColor: color}]}>
              <IconButton
                name="arrow-right"
                onPress={() => onButtonPressed(selectedIndex)}
                color="#FFF3"
              />
              <Text style={styles.title}>{title}</Text>
              <Icon
                type="MaterialCommunityIcons"
                name={icon}
                style={styles.headerSelectionIcon}
              />
            </View>
            <View style={styles.content}>{selected}</View>
          </View>
        </Animated.View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    elevation: 30,
    zIndex: 30,
  },
  header: {
    height: 56,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: 10,
  },
  fillUpperNotch: {
    position: 'absolute',
    width: CONTENT_WIDTH,
    height: 400,
    top: -400,
    right: 0,
  },
  headerSelectionIcon: {
    color: 'white',
    fontSize: 32,
  },
  panel: {
    position: 'absolute',
    width: FULL_WIDTH,
    height: '100%',
    right: 0,
    top: 0,
    flex: 1,
  },
  content: {
    backgroundColor: 'white',
    flex: 1,
  },
  title: {
    color: 'white',
    fontSize: 22,
  },
  animatedArea: {
    position: 'absolute',
    width: FULL_WIDTH,
    height: '100%',
  },
  tabContainer: {
    position: 'absolute',
    left: 0,
    width: TAB_WIDTH,
    height: '100%',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  contentContainer: {
    position: 'absolute',
    left: TAB_WIDTH,
    width: CONTENT_WIDTH,
    height: '100%',
  },
  tabButton: {
    marginTop: 10,
  },
});

SidePanel.defaultProps = {
  style: {},
  children: undefined,
};

SidePanel.propTypes = {
  style: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.number,
    PropTypes.array,
  ]),
  children: PropTypes.node,
};

export default SidePanel;
